import {
  defaultDropAnimation,
  DropAnimation,
  MeasuringStrategy,
  Modifier,
} from '@dnd-kit/core';
import { AnimateLayoutChanges, arrayMove } from '@dnd-kit/sortable';
import { AGREEMENT_TYPE } from 'services/templates/agreement-template/types';

import {
  IContractTemplateForm,
  IDistributionType,
  TEMPLATE_TYPE,
  type IContractTemplateFlattenedTreeForm,
  type IContractTemplateTreeItemForm,
} from 'services/templates/types';
import { INewIdsGenerator } from 'types/contract/dnd/dndUtils.interface';

const getDragDepth = (offset: number, indentationWidth: number) => {
  return Math.round(offset / indentationWidth);
};

export const getProjection = (
  items: IContractTemplateFlattenedTreeForm[],
  activeId: string,
  overId: string,
  dragOffset: number,
  indentationWidth: number,
  initialMinDepth: number = 0,
) => {
  const overItemIndex = items.findIndex(({ id }) => id === overId);
  const activeItemIndex = items.findIndex(({ id }) => id === activeId);
  const activeItem = items[activeItemIndex];
  const newItems = arrayMove(items, activeItemIndex, overItemIndex);
  const previousItem = newItems[overItemIndex - 1];
  const nextItem = newItems[overItemIndex + 1];
  const dragDepth = getDragDepth(dragOffset, indentationWidth);
  const projectedDepth = activeItem.depth + dragDepth;
  const maxDepth = getMaxDepth({
    previousItem,
  });

  const minDepth =
    initialMinDepth > getMinDepth({ nextItem })
      ? initialMinDepth
      : getMinDepth({
          nextItem,
        });
  let depth = projectedDepth;

  if (projectedDepth >= maxDepth) {
    depth = maxDepth;
  } else if (projectedDepth < minDepth) {
    depth = minDepth;
  }
  const getParentId = () => {
    if (depth === 0 || !previousItem) {
      return null;
    }

    if (depth === previousItem.depth) {
      return previousItem.parentId;
    }

    if (depth > previousItem.depth) {
      return previousItem.id;
    }

    const newParent = newItems
      .slice(0, overItemIndex)
      .reverse()
      .find(item => item.depth === depth)?.parentId;

    return newParent ?? null;
  };

  return { depth, maxDepth, minDepth, parentId: getParentId() };
};

const getMaxDepth = ({
  previousItem,
}: {
  previousItem: IContractTemplateFlattenedTreeForm;
}) => {
  if (previousItem) {
    return previousItem.type === TEMPLATE_TYPE.section ||
      previousItem.type === TEMPLATE_TYPE.digitalDistribution ||
      previousItem.type === TEMPLATE_TYPE.paragraphSection
      ? previousItem.depth + 1
      : previousItem.depth;
  }

  return 0;
};

const getMinDepth = ({
  nextItem,
}: {
  nextItem: IContractTemplateFlattenedTreeForm;
}) => {
  if (nextItem) {
    return nextItem.depth;
  }

  return 0;
};

export const flattenTree = (
  items: IContractTemplateTreeItemForm[],
  parentId: string | null = null,
  depth = 0,
): IContractTemplateFlattenedTreeForm[] => {
  return items.reduce<IContractTemplateFlattenedTreeForm[]>(
    (
      acc: IContractTemplateFlattenedTreeForm[],
      item: IContractTemplateTreeItemForm,
      index: number,
    ) => {
      return [
        ...acc,
        { ...item, parentId, depth, index },
        ...flattenTree(item?.children ?? [], item.id, depth + 1),
      ];
    },
    [],
  );
};

export const buildTree = (
  flattenedItems: IContractTemplateFlattenedTreeForm[],
): IContractTemplateTreeItemForm[] => {
  const root: IContractTemplateTreeItemForm = {
    id: 'root',
    children: [],
    type: TEMPLATE_TYPE.contract,
    content: '',
  };
  const nodes: Record<string, IContractTemplateTreeItemForm> = {
    [root.id]: root,
  };

  const items = flattenedItems.map(item => ({ ...item, children: [] }));

  for (const item of items) {
    const { id, children, type, content } = item;
    const parentId = item.parentId ?? root.id;
    const parent = nodes[parentId] ?? findItem(items, parentId);

    nodes[id] = { id, children, type, content } as any;
    parent.children.push(item);
  }

  return root.children;
};

export const findItem = (
  items: IContractTemplateTreeItemForm[],
  itemId: string,
) => {
  return items.find(({ id }) => id === itemId);
};

export const findItemDeep = (
  items: IContractTemplateTreeItemForm[],
  itemId: string,
): IContractTemplateTreeItemForm | undefined => {
  for (const item of items) {
    const { id, children } = item;

    if (id === itemId) {
      return item;
    }

    if (children.length) {
      const child = findItemDeep(children, itemId);

      if (child) {
        return child;
      }
    }
  }

  return undefined;
};

export const getExtremeId = (
  items: IContractTemplateTreeItemForm[],
  highest: boolean = true,
): string => {
  const flattenedTree = flattenTree(items);

  const sortedTree = flattenedTree
    .filter(item => !Number.isNaN(parseInt(item.id)) && item.id.length !== 36)
    .sort((a, b) => {
      const aId = parseInt(a.id);
      const bId = parseInt(b.id);
      if (aId > bId) {
        return -1;
      } else if (aId < bId) {
        return 1;
      }
      return 0;
    });

  return sortedTree[highest ? 0 : flattenedTree.length - 1].id;
};

export const getMissingIds = (items: IContractTemplateTreeItemForm[]) => {
  const missing = new Array<number>();

  const itemsId = flattenTree(items).map(item => parseInt(item.id));

  for (let i = 1; i <= parseInt(getExtremeId(items)); i++) {
    if (itemsId.indexOf(i) == -1) {
      missing.push(i);
    }
  }

  return missing.map(id => `${id}`);
};

export const findLowestAvailableId = (
  items: IContractTemplateTreeItemForm[],
): string => {
  const missingIds = getMissingIds(items);

  if (missingIds.length) {
    return missingIds[0];
  }
  return `${parseInt(getExtremeId(items)) + 1}`;
};

export const generateNewIds = (
  items: IContractTemplateTreeItemForm[],
): INewIdsGenerator => ({
  missing: getMissingIds(items).map(id => parseInt(id)),
  extreme: parseInt(getExtremeId(items)),
});

export const removeItem = (
  items: IContractTemplateTreeItemForm[],
  id: string,
) => {
  const newItems = [];

  for (const item of items) {
    if (item.id === id) {
      continue;
    }

    if (item.children.length) {
      item.children = removeItem(item.children, id);
    }

    newItems.push(item);
  }

  return newItems;
};

export const addItem = (
  items: IContractTemplateTreeItemForm[],
  parentId: string,
  initialItem: IContractTemplateTreeItemForm,
) => {
  const newItems = [];

  for (const item of items) {
    const childrenLength = item.children.length;
    if (item.id === parentId && item.type !== TEMPLATE_TYPE.agreement) {
      item.children[childrenLength] = initialItem;
    }

    if (childrenLength) {
      item.children = addItem(item.children, parentId, initialItem);
    }

    newItems.push(item);
  }

  return newItems;
};

export const setProperty = <T extends keyof IContractTemplateTreeItemForm>(
  items: IContractTemplateTreeItemForm[],
  id: string,
  property: T,
  setter: (
    value: IContractTemplateTreeItemForm[T],
  ) => IContractTemplateTreeItemForm[T],
) => {
  for (const item of items) {
    if (item.id === id) {
      item[property] = setter(item[property]);
      continue;
    }

    if (item.children.length) {
      item.children = setProperty(item.children, id, property, setter);
    }
  }

  return [...items];
};

const countChildren = (
  items: IContractTemplateTreeItemForm[],
  count = 0,
): number => {
  return items.reduce((acc, { children }) => {
    if (children.length) {
      return countChildren(children, acc + 1);
    }

    return acc + 1;
  }, count);
};

export const getChildCount = (
  items: IContractTemplateTreeItemForm[],
  id: string,
) => {
  if (!id) {
    return 0;
  }

  const item = findItemDeep(items, id);

  return item ? countChildren(item.children) : 0;
};

export const removeChildrenOf = (
  items: IContractTemplateFlattenedTreeForm[],
  ids: string[],
) => {
  const excludeParentIds = [...ids];

  return items.filter(item => {
    if (item.parentId && excludeParentIds.includes(item.parentId)) {
      if (item.children.length) {
        excludeParentIds.push(item.id);
      }
      return false;
    }

    return true;
  });
};

export const generateFormikNames = (
  items: IContractTemplateTreeItemForm[],
  basePath = '',
): Record<string, string> => {
  const paths: Record<string, string> = {};

  items.forEach((item, index) => {
    const currentPath = basePath
      ? `${basePath}.children[${index}]`
      : `[${index}]`;

    paths[item.id] = currentPath;

    if (item.children && item.children.length > 0) {
      Object.assign(paths, generateFormikNames(item.children, currentPath));
    }
  });

  return paths;
};

export const measuring = {
  droppable: {
    strategy: MeasuringStrategy.Always,
  },
};

export const dropAnimation: DropAnimation = {
  ...defaultDropAnimation,
};

export const adjustTranslate: Modifier = ({ transform }) => {
  return {
    ...transform,
    y: transform.y - 15,
  };
};

export const animateLayoutChanges: AnimateLayoutChanges = ({
  isSorting,
  wasDragging,
}) => (isSorting || wasDragging ? false : true);

export const getDistributionSection = (
  values: IContractTemplateForm,
  type?: IDistributionType,
) => values.items[0]?.children?.find(item => item.type === type);

const DISTRIBUTION_SECTION_TYPES: IDistributionType[] = [
  TEMPLATE_TYPE.digitalDistribution,
  TEMPLATE_TYPE.physicalDistribution,
  TEMPLATE_TYPE.merchDistribution,
  TEMPLATE_TYPE.channelDistribution,
];

export const isDistributionSectionType = (
  type: TEMPLATE_TYPE
): type is IDistributionType =>
  DISTRIBUTION_SECTION_TYPES.includes(type as IDistributionType);

export const getDistributionSectionParent = (
  items: IContractTemplateFlattenedTreeForm[],
  itemId: string
): IContractTemplateFlattenedTreeForm | undefined => {
  let current = items.find(item => item.id === itemId);

  while (current) {
    if (isDistributionSectionType(current.type)) {
      return current;
    }

    if (!current.parentId) {
      return undefined;
    }

    current = items.find(item => item.id === current!.parentId);
  }

  return undefined;
};

export const canMoveShareToParent = (
  items: IContractTemplateFlattenedTreeForm[],
  activeItem: IContractTemplateFlattenedTreeForm,
  targetParentId: string | null
): boolean => {
  if (
    activeItem.type !== TEMPLATE_TYPE.agreement ||
    activeItem.content?.type !== AGREEMENT_TYPE.shares
  ) {
    return true;
  }

  const sourceSection = getDistributionSectionParent(items, activeItem.id);
  if (!sourceSection || !targetParentId) {
    return true;
  }

  const targetParent = items.find(item => item.id === targetParentId);
  const targetSection =
    targetParent && isDistributionSectionType(targetParent.type)
      ? targetParent
      : getDistributionSectionParent(items, targetParentId);

  return !targetSection || sourceSection.id === targetSection.id;
};

export const getSelectedDistributionSharesIds = (
  values: IContractTemplateForm,
  type?: IDistributionType,
) =>
  getDistributionSection(values, type)?.children?.reduce(
    (acc: string[], item: IContractTemplateTreeItemForm) => {
      if (
        item.type === TEMPLATE_TYPE.agreement &&
        item.content.type === AGREEMENT_TYPE.shares &&
        item.content.distributionTemplateShares
      ) {
        acc.push(item.content.distributionTemplateShares.id);
      }
      return acc;
    },
    [],
  ) ?? [];
